<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">
                <span v-show="!loading && !categoryForm.hasId()">Nova</span>
                <span v-show="!loading && categoryForm.hasId()">Edição de</span>
                <span> Categoria</span>
            </p>
        </div>

        <div class="muven-content">
            <Breadcrumb :items="breadcrumbList" v-show="!loading" class="mb-3"/>

            <Loading page v-show="loading" />

            <div v-show="!loading">

                <div class="muven-tip muven-tip-standalone tip-info mb-3" v-show="categoryForm.hasIdSource()">
                    <fa icon="info" class="tip-icon" />
                    <p>Esta categoria foi cadastrada pela retaguarda, portanto algumas informações não podem ser alteradas.</p>
                </div>

                <div class="muven-card muven-card-md muven-card-centered">
                    <div class="muven-card-loading" v-show="submittingCategory">
                        <div class="muven-card-loading-message-container">
                            <p class="loading-message">
                                <fa class="loading-icon" icon="sync-alt" spin />
                            </p>
                        </div>
                    </div>

                    <div class="muven-card-title">
                        <p>{{categoryForm.name | infoOrPlaceholder}}</p>
                    </div>

                    <div>
                        <div class="row">
                            <div class="col-12 form-group">
                                <label>Nome</label>
                                <input type="text" 
                                    class="form-control"
                                    :disabled="categoryForm.hasIdSource()"
                                    v-model="categoryForm.name" />
                            </div>

                            <div class="col-12 form-group">
                                <label>Título</label>
                                <input type="text" 
                                    class="form-control"
                                    v-model="categoryForm.title" />
                            </div>

                            <div class="col-12 form-group">
                                <label>Descrição</label>
                                <input type="text" 
                                    class="form-control"
                                    v-model="categoryForm.description" />
                            </div>

                            <div class="col-12 form-group">
                                <label>Palavras chave</label>
                                <input type="text" 
                                    class="form-control"
                                    v-model="categoryForm.keywords" />
                            </div>

                            <div class="col-12 form-group">
                                <label>Categoria pai</label>
                                <SelectWrapperCmp 
                                    :show-remove-button="categoryForm.hasParent()"
                                    :disabled="categoryForm.hasIdSource()"
                                    @removed="handleCategoryParentRemoved">
                                        <template v-slot:select>
                                            <multiselect
                                                v-model="categoryForm.parent"
                                                :options="categoryList"
                                                :multiple="false"
                                                :show-labels="false"
                                                :disabled="categoryForm.hasIdSource()"
                                                @select="parentCategorySelected"
                                                label="name"
                                                track-by="id"
                                                placeholder="" />
                                        </template>
                                </SelectWrapperCmp>
                            </div>

                            <div class="col-12 col-md-6">
                                <SwitchToggle 
                                    :prop-check="categoryForm.isActive"
                                    :label-checked="'Ativa'"
                                    :label-unchecked="'Inativa'"
                                    @updated="isActiveHandler"
                                />

                                <SwitchToggle 
                                    :prop-check="categoryForm.allowExport"
                                    :label-checked="'Exportar para os canais'"
                                    :label-unchecked="'Exportar para os canais'"
                                    @updated="allowExportHandler"
                                />
                            </div>
                        </div>

                        <div class="muven-card-actions">
                            <button class="btn btn-primary"
                                :disabled="submittingCategory || !categoryForm.isValid()"                                
                                @click="submitCategory(categoryForm)">
                                Gravar

                                <Loading v-show="submittingCategory" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import categoryService from '../services/CategoryService'
import { flattenTree } from '@/utils/normalizers/TreeNormalizer'
import { infoOrPlaceholder } from '@/utils/filters/InformationOrPlaceholderFilter'
import SelectWrapperCmp from '@/utils/components/SelectWrapperCmp'
import { CategoryFormModel } from '../models/CategoryFormModel'

export default {
    name: 'CategoryFormView',
    data() {
        return {
            loading: true,
            submittingCategory: false,
            categoryForm: new CategoryFormModel(),
            categoryList: [],
            breadcrumbList: [],
        }
    },
    components: {
        SelectWrapperCmp,
    },
    filters: {
        infoOrPlaceholder,
    },
    methods: {
        async submitCategory(cat) {
            this.$floatingAlert.hide()

            this.submittingCategory = true

            const catNormalized = CategoryFormModel.normalizeBeforeSubmit(cat)
            
            try {
                const catFromServer = await categoryService.createOrUpdateCategory(catNormalized)

                this.$router.push(`/categorias/${catFromServer.id}`)

                if (!catNormalized.hasId()) {
                    this.$floatingAlert.success({
                        message: `Categoria <b>${catNormalized.name}</b> criada com sucesso!`,
                    })
                } else {
                    this.$floatingAlert.success({
                        message: `Categoria <b>${catNormalized.name}</b> modificada com sucesso!`,
                    })
                }
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.submittingCategory = false
        },
        handleCategoryParentRemoved() {
            this.categoryForm.removeParent()
        },
        isActiveHandler(info) {
            this.categoryForm.isActive = info
        },
        allowExportHandler(info) {
            this.categoryForm.allowExport = info
        },
        parentCategorySelected(cat) {
            if (!!cat) {
                this.categoryForm.idParent = cat.id
            } else {
                this.categoryForm.idParent = undefined
            }
        },
    },
    async mounted() {
        try {
            const categoryTree = await categoryService.getCategoryTree()

            if (!!this.$route.params.id) {
                const category = await categoryService.getCategoryById(this.$route.params.id)

                this.categoryForm = new CategoryFormModel(category)
                this.categoryList = flattenTree(categoryTree).filter((cat) => {
                    return String(cat.id) !== String(this.categoryForm.id)
                })

                this.breadcrumbList = [
                    {
                        id: 0,
                        description: 'Categorias',
                        path: '/categorias',
                    },
                    {
                        id: 1,
                        description: 'Detalhes',
                        path: `/categorias/${this.categoryForm.id}`,
                    },
                    {
                        id: 1,
                        description: `Edição de #${this.categoryForm.id}`,
                        path: '',
                    },
                ]
            } else {
                this.categoryList = flattenTree(categoryTree)

                this.breadcrumbList = [
                    {
                        id: 0,
                        description: 'Categorias',
                        path: '/categorias',
                    },
                    {
                        id: 1,
                        description: 'Nova categoria',
                        path: '',
                    },
                ]
            }
        } catch (err) {
            this.$floatingAlert.error({
                message: err.message,
            })
        }

        this.loading = false
    },
}
</script>



